import { useEffect, useRef } from 'react'
import { site } from '../config/site'
import { useReducedMotion } from '../hooks/useReducedMotion'
import { usePlanTripCta } from '../hooks/usePlanTripCta'
import { Icon } from './ui/Icon'

/**
 * Closing banner, mounted last, just before the footer: the same scroll-
 * scrubbed pinned-video treatment as JourneyShowcase (a Taj Mahal sequence
 * mid-page) — here with travellers arriving at a Mediterranean coastline.
 * Scroll drives the video's exact frame, matching the rest of the site's
 * signature interaction instead of just playing on its own.
 *
 * Timeline: entrance (left-edge wipe) rides the section's APPROACH so the
 * stage is never blank; the pinned range scrubs the video 0 → duration over
 * its first 80%, then spends its last 20% on the exit (wipe sliding away to
 * the right, crossfading to the page surface) before releasing into the
 * footer. The exit deliberately lives INSIDE the pinned range rather than
 * riding a post-unpin scroll distance — the footer is shorter than one
 * viewport on most screens, so there isn't always enough room below the
 * section to scroll a post-unpin exit to completion.
 *
 * CTA_POSTER still shows a frame from the old clip — no ffmpeg is available
 * in this environment to cut a new poster from CTA_VIDEO. It only shows
 * briefly before the video loads, and as the reduced-motion fallback image
 * (see StaticCta below), so it should be regenerated from the new clip when
 * ffmpeg tooling is available; scripts/generate-placeholders.mjs is the
 * existing convention for this.
 */
const CTA_VIDEO = '/videos/closing-cta-mediterranean.mp4'
const CTA_POSTER = '/videos/closing-cta-poster.jpg'

/** `onDark` renders the white-on-video treatment for the full-bleed stage. */
function ClosingCopy({ onDark = false }) {
  const { eyebrow, headline, highlight, body, cta } = site.closingCta
  const planTripCta = usePlanTripCta()
  return (
    <>
      <p
        className={`text-[0.72rem] font-bold tracking-[0.22em] uppercase sm:text-sm ${
          onDark ? 'text-white/75' : 'text-brand-600 dark:text-brand-300'
        }`}
      >
        {eyebrow}
      </p>
      <h2 className={`mt-5 text-[clamp(2.2rem,5vw,4rem)] leading-[1.05] font-bold ${onDark ? 'text-white' : ''}`}>
        {headline[0]}
        <br />
        <span className={onDark ? 'text-brand-300' : 'text-brand-600 dark:text-brand-300'}>{highlight}</span>
      </h2>
      <p className={`mt-6 max-w-[42ch] text-base leading-relaxed sm:text-lg ${onDark ? 'text-white/85' : ''}`}>
        {body}
      </p>
      <a
        href={planTripCta.href ?? cta.href}
        onClick={planTripCta.onClick}
        className={`group mt-8 inline-flex items-center gap-2.5 rounded-full px-7 py-4 text-base font-semibold shadow-xl transition-[background-color,transform] duration-200 hover:-translate-y-0.5 ${
          onDark
            ? 'bg-white text-brand-700 shadow-black/25 hover:bg-brand-50'
            : 'bg-brand-600 text-white shadow-brand-600/25 hover:bg-brand-700'
        }`}
      >
        <Icon name="send" className="h-[1.15rem] w-[1.15rem]" />
        {cta.label}
      </a>
    </>
  )
}

/** Reduced-motion path: no pinning, no scrubbing — the poster frame as a still banner. */
function StaticClosingCta() {
  return (
    <section className="relative flex min-h-[75vh] items-center overflow-hidden bg-ink-950 sm:min-h-[65vh]">
      <img
        src={CTA_POSTER}
        alt=""
        aria-hidden="true"
        className="absolute inset-0 h-full w-full object-cover object-[50%_20%]"
      />
      <div className="pointer-events-none absolute inset-0 bg-linear-to-t from-ink-950/85 via-ink-950/20 to-ink-950/35 md:bg-linear-to-r md:from-ink-950/85 md:via-ink-950/30 md:via-40% md:to-transparent md:to-75%" />
      <div className="relative mx-auto w-full max-w-[88rem] px-5 py-20 sm:px-8">
        <div className="max-w-2xl">
          <ClosingCopy onDark />
        </div>
      </div>
    </section>
  )
}

export function ClosingCta() {
  const reducedMotion = useReducedMotion()
  const containerRef = useRef(null)
  const videoRef = useRef(null)
  const cardRef = useRef(null)
  const copyRef = useRef(null)
  const copyLightRef = useRef(null)
  const bleedRef = useRef(null)

  useEffect(() => {
    if (reducedMotion) return
    const container = containerRef.current
    const video = videoRef.current
    if (!container || !video) return

    let objectUrl = null
    let cancelled = false
    let duration = 0
    let current = 0
    let target = 0
    let lastSet = -1
    let raf = 0

    const clamp01 = (v) => Math.min(1, Math.max(0, v))
    // Entrance (`approach`) still rides on scrolling INTO view, using
    // whatever section precedes this one for scroll room. The exit wipe used
    // to mirror that with a post-unpin `leave` measure (rect.bottom vs vh) —
    // but that needs a full viewport height of scrollable content AFTER the
    // section to ever complete, and this section is followed by the footer,
    // which is shorter than the viewport on most screens. It could get stuck
    // most of the way through the wipe with no way to scroll further. The
    // exit now instead spends the LAST slice of the section's own pinned
    // range (p in [0.8, 1]) — guaranteed available regardless of what
    // follows, since it's entirely inside this section's own height budget.
    const measure = () => {
      const rect = container.getBoundingClientRect()
      const vh = window.innerHeight
      const range = rect.height - vh
      return {
        approach: clamp01((vh - rect.top) / vh),
        p: range > 0 ? clamp01(-rect.top / range) : 0,
      }
    }

    const tick = () => {
      raf = 0
      const { approach, p } = measure()

      const e = 1 - Math.pow(1 - approach, 3)
      // Ease-IN here (not the ease-out used everywhere else in this file):
      // an ease-out crossfade reaches ~90% white by the midpoint of this
      // window, leaving a long dead stretch of flat white scrolling before
      // the section actually unsticks into the footer. Ease-in keeps the
      // video/copy visible through most of the window and only wipes to
      // white right at the handoff.
      const l = Math.pow(clamp01((p - 0.8) / 0.2), 3)
      if (cardRef.current) {
        cardRef.current.style.clipPath = `inset(0 ${((1 - e) * 100).toFixed(2)}% 0 ${(l * 100).toFixed(2)}%)`
      }
      video.style.transform = `translate3d(${(-(1 - e) * 8 + l * 8).toFixed(2)}%, 0, 0)`
      if (copyRef.current) {
        const c = 1 - Math.pow(1 - clamp01((approach - 0.15) / 0.85), 3)
        // Crossfades into copyLightRef below rather than fading to nothing:
        // this section's own height (h-screen while pinned) needs a full
        // viewport of scroll to slide off-screen once unpinned, but the
        // footer right after it is much shorter than a viewport. At the
        // very bottom of the page the browser has nowhere left to scroll,
        // so this panel's final frame stays on screen — for a while — no
        // matter how the exit is timed. Fading the copy to nothing there
        // used to leave that unavoidable stretch completely blank; crossing
        // over to a light-on-white copy instead means it reads as an
        // intentional closing panel, not a broken empty page.
        copyRef.current.style.opacity = String(c * (1 - l))
        copyRef.current.style.transform = `translateY(${((1 - c) * 34).toFixed(1)}px)`
        if (copyLightRef.current) copyLightRef.current.style.opacity = String(c * l)

        // Whichever layer is (near-)invisible still sits in the hit-testing
        // and tab order otherwise: opacity: 0 doesn't remove an element from
        // pointer/keyboard/screen-reader interaction. Without this, mouse
        // users hover the invisible layer's button and never see the visible
        // one's hover state, keyboard users tab through two duplicate links,
        // and screen readers announce the heading/body/link twice.
        // "auto"/"none" explicitly, not "" — copyLightRef carries the static
        // `pointer-events-none` Tailwind class below, so clearing the inline
        // style back to "" only removes the override and leaves that class
        // in charge, meaning the light copy's button (the landing page's
        // last CTA) never became clickable no matter which layer was
        // supposedly in front.
        const lightIsFront = l > 0.5
        copyRef.current.style.pointerEvents = lightIsFront ? "none" : "auto"
        copyRef.current.inert = lightIsFront
        if (copyLightRef.current) {
          copyLightRef.current.style.pointerEvents = lightIsFront ? "auto" : "none"
          copyLightRef.current.inert = !lightIsFront
        }
      }
      // The dark stage is only needed while the white copy/video are still
      // visible. As the exit wipe closes (l → 1, copy already faded by the
      // same factor) crossfade the exposed backing colour to the page
      // surface, so the tail end of the section reads as clean white/light
      // instead of a dark bar cutting hard into the footer.
      if (bleedRef.current) bleedRef.current.style.opacity = String(l)

      if (!duration) return
      target = clamp01(p / 0.8) * duration
      current += (target - current) * 0.22
      if (Math.abs(target - current) < 0.003) current = target
      if (!video.seeking && Math.abs(current - lastSet) > 1 / 60) {
        video.currentTime = current
        lastSet = current
      }
      if (current !== target || video.seeking) schedule()
    }
    const schedule = () => {
      if (!raf) raf = requestAnimationFrame(tick)
    }

    const onMetadata = () => {
      duration = video.duration || 0
      current = target = clamp01(measure().p / 0.8) * duration
      lastSet = -1
      schedule()
    }

    video.addEventListener('loadedmetadata', onMetadata)
    video.addEventListener('seeked', schedule)
    // Direct URL first so the opening frame paints immediately; the blob is
    // fetched in the background purely as scrub-reliability insurance and
    // only takes over if the direct source never becomes ready.
    video.src = CTA_VIDEO
    fetch(CTA_VIDEO)
      .then((res) => {
        if (!res.ok || !(res.headers.get('content-type') || '').startsWith('video')) {
          throw new Error(`unexpected response for ${CTA_VIDEO}`)
        }
        return res.blob()
      })
      .then((blob) => {
        if (cancelled) return
        if (video.readyState === 0 || video.error) {
          objectUrl = URL.createObjectURL(blob)
          video.src = objectUrl
        }
      })
      .catch(() => {})

    tick()
    window.addEventListener('scroll', schedule, { passive: true })
    window.addEventListener('resize', schedule)
    return () => {
      cancelled = true
      if (raf) cancelAnimationFrame(raf)
      window.removeEventListener('scroll', schedule)
      window.removeEventListener('resize', schedule)
      video.removeEventListener('loadedmetadata', onMetadata)
      video.removeEventListener('seeked', schedule)
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [reducedMotion])

  if (reducedMotion) return <StaticClosingCta />

  return (
    <section ref={containerRef} className="relative h-[220vh] md:h-[260vh]" aria-label={site.closingCta.eyebrow}>
      {/* Always dark — the overlay copy is permanently white (no adaptive
          light-mode variant), so the exposed backing colour during the
          entrance/exit wipes must stay dark too, or white text goes
          unreadable over a light gap while the video is still sliding in. */}
      <div className="sticky top-0 h-screen overflow-hidden bg-ink-950">
        {/* Full-bleed video stage — wipes in from the left, then scroll
            scrubs its timeline. No autoplay/loop: scroll owns the timeline. */}
        <div
          ref={cardRef}
          className="absolute inset-0 will-change-[clip-path]"
          style={{ clipPath: 'inset(0 100% 0 0)' }}
        >
          <video
            ref={videoRef}
            poster={CTA_POSTER}
            muted
            playsInline
            preload="auto"
            disablePictureInPicture
            tabIndex={-1}
            aria-hidden="true"
            // The AI-companion frame at the start of the clip is composed
            // with her face high in the shot; on wide/short viewports
            // object-cover's default centre crop clips it off. Biasing the
            // vertical anchor toward the top keeps the face in frame across
            // the scrub without noticeably cutting the later beach scenes,
            // which are already centred lower.
            className="absolute inset-0 h-full w-full object-cover object-[50%_20%] will-change-transform"
          />
          <div className="pointer-events-none absolute inset-0 bg-linear-to-t from-ink-950/85 via-ink-950/20 to-ink-950/35 md:bg-linear-to-r md:from-ink-950/85 md:via-ink-950/30 md:via-40% md:to-transparent md:to-75%" />
        </div>

        {/* Crossfades the dark stage to the page surface as the exit wipe
            finishes, so the section tail blends into the footer instead of
            cutting hard from ink-950 to it. Opaque, so it also fully hides
            the (by-then invisible) video/scrim once revealed. */}
        <div ref={bleedRef} className="pointer-events-none absolute inset-0 bg-[var(--surface)] opacity-0" />

        {/* Overlaid copy. copyLightRef is stacked on top of copyRef at the
            same position and crossfades in as copyRef fades out — see the
            tick() comment above for why this can't just fade to nothing. */}
        <div className="relative mx-auto flex h-full max-w-[88rem] items-end px-5 pt-24 pb-20 sm:px-8 md:items-center md:pb-16">
          <div className="relative w-full max-w-2xl">
            <div ref={copyRef} className="opacity-0 will-change-[opacity,transform]">
              <ClosingCopy onDark />
            </div>
            <div
              ref={copyLightRef}
              className="absolute inset-0 opacity-0 pointer-events-none will-change-[opacity]"
              inert
            >
              <ClosingCopy />
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
